import { config } from '@/runtime-config';
import axios, { type AxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/store/authStore';

interface ExtendedConfig extends AxiosRequestConfig {
  skipAuth?: boolean;
  _retryAfterRefresh?: boolean;
}

const NON_FATAL_401_PATHS = [
  '/ums/auth/access-status',
  '/ums/auth/register-organization',
  '/incidents/',
];

function isNonFatalUnauthorizedPath(url?: string): boolean {
  if (!url) {
    return false;
  }
  return NON_FATAL_401_PATHS.some((path) => url.includes(path));
}

function resolveBaseUrl(): string {
  const raw = config('API_BASE_URL', import.meta.env.VITE_API_BASE_URL).trim();

  const ensureApiSuffix = (value: string): string => {
    const withoutTrailingSlash = value.replace(/\/+$/, '');
    return withoutTrailingSlash.endsWith('/api')
      ? withoutTrailingSlash
      : `${withoutTrailingSlash}/api`;
  };

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return ensureApiSuffix(raw);
  }

  // Empty VITE_API_BASE_URL → use relative /api so Vite dev proxy intercepts the requests.
  return '/api';
}

const BASE_URL = resolveBaseUrl();

// Without an explicit timeout axios waits forever, so a stalled backend leaves
// the UI spinning with no error state. 30s is above our slowest normal request.
const REQUEST_TIMEOUT_MS = Number(import.meta.env.VITE_API_TIMEOUT_MS) || 30_000;
const apiAxios = axios.create({ baseURL: BASE_URL, timeout: REQUEST_TIMEOUT_MS });
let isLoggingOut = false;

function normalizeUrl(url: string): string {
  const base = BASE_URL.replace(/\/+$/, '');
  const normalized = url.trim();

  if (!normalized.startsWith('/')) {
    return normalized;
  }

  const baseEndsWithApi = base.endsWith('/api');
  if (baseEndsWithApi && normalized.startsWith('/api/')) {
    return normalized.slice('/api'.length);
  }

  return normalized;
}

apiAxios.interceptors.request.use(async (config) => {
  const skipAuth = (config as ExtendedConfig).skipAuth;
  if (!skipAuth) {
    const token = await useAuthStore.getState().getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

apiAxios.interceptors.response.use(
  (res) => res.data,
  async (err) => {
    const status = err?.response?.status;
    const config = err?.config as ExtendedConfig | undefined;
    const skipAuth = Boolean(config?.skipAuth);
    const requestUrl = typeof config?.url === 'string' ? config.url : undefined;
    const headers = config?.headers as { Authorization?: string; authorization?: string } | undefined;
    const authHeaderValue = headers?.Authorization ?? headers?.authorization;
    const hadBearerHeader =
      typeof authHeaderValue === 'string' && authHeaderValue.startsWith('Bearer ');

    const shouldHandleUnauthorized = (
      status === 401
      && !skipAuth
      && hadBearerHeader
      && !isNonFatalUnauthorizedPath(requestUrl)
    );

    if (shouldHandleUnauthorized && config && !config._retryAfterRefresh) {
      config._retryAfterRefresh = true;
      const refreshedToken = await useAuthStore.getState().getToken();
      if (refreshedToken) {
        const nextHeaders = {
          ...(config!.headers ?? {}),
          Authorization: `Bearer ${refreshedToken}`,
        };
        return apiAxios.request({ ...config, headers: nextHeaders });
      }
    }

    if (shouldHandleUnauthorized && !isLoggingOut) {
      isLoggingOut = true;
      useAuthStore.getState().logout();
    }

    return Promise.reject(err);
  },
);

export const apiClient = {
  get: async <T>(url: string, config?: ExtendedConfig): Promise<T> =>
    (await apiAxios.get(normalizeUrl(url), config)) as unknown as T,
  post: async <T>(url: string, data?: any, config?: ExtendedConfig): Promise<T> =>
    (await apiAxios.post(normalizeUrl(url), data, config)) as unknown as T,
  put: async <T>(url: string, data?: any, config?: ExtendedConfig): Promise<T> =>
    (await apiAxios.put(normalizeUrl(url), data, config)) as unknown as T,
  patch: async <T>(url: string, data?: any, config?: ExtendedConfig): Promise<T> =>
    (await apiAxios.patch(normalizeUrl(url), data, config)) as unknown as T,
  delete: async <T>(url: string, config?: ExtendedConfig): Promise<T> =>
    (await apiAxios.delete(normalizeUrl(url), config)) as unknown as T,
};

export default apiClient;
